import { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Snackbar, SnackbarCloseReason, Typography } from '@mui/material';
import * as yup from 'yup';
import { VTextField, VFieldMoneyNumber4dn, VForm, useVForm, IVFormErrors } from '../../../shared/forms';
import { IOrdemServicoMaoDeObra, OrdemServicoMaoDeObraService } from '../../../shared/services/api/servicos/OrdemServicoMaoDeObraService';

interface IFormData {
  mecanico: string;
  descricao: string;
  valor: number;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  mecanico: yup.string().required('Informe o nome do mecânico').min(1),
  descricao: yup.string().required('Informe o serviço realizado').min(1),
  valor: yup.number().required('Informe o valor').min(0),
});

interface IDialogMaoDeObraOSProps {
  open: boolean;
  idos: string;
  onClose: () => void;
  aoSalvar: (item: IOrdemServicoMaoDeObra) => void;
}

// Adicionar uma linha de mão de obra na OS — mecânico é texto livre (não
// vínculo com usuário do sistema, ver comentário no provider Create.ts do
// backend), então cada mecânico que trabalhou na OS aparece como sua
// própria linha, com o serviço que fez e o valor cobrado por isso. Mesmo
// padrão de DialogItemOS.tsx: item já sai salvo direto no backend, dialog
// fica aberta pra adicionar o próximo.
export const DialogMaoDeObraOS: React.FC<IDialogMaoDeObraOSProps> = ({ open, idos, onClose, aoSalvar }) => {
  const { formRef, save } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [valor, setValor] = useState(0);

  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error'>('warning');

  useEffect(() => {
    if (!open) return;
    setValor(0);
    formRef.current?.setData({ mecanico: '', descricao: '', valor: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const handleSave = (dados: IFormData) => {
    formValidationSchema
      .validate(dados, { abortEarly: false })
      .then(() => {
        setIsLoading(true);
        OrdemServicoMaoDeObraService
          .create(idos, {
            mecanico: dados.mecanico.trim(),
            descricao: dados.descricao.trim(),
            valor: dados.valor,
          })
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setTms('error');
              setMsg(result.message);
              setOpenmsg(true);
            } else {
              aoSalvar(result);
              setValor(0);
              formRef.current?.setData({ mecanico: '', descricao: '', valor: '' });
            }
          });
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Adicionar Mão de Obra</DialogTitle>
      <DialogContent dividers>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={null} onClose={handleCloseMsg}>
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>
        <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <Box>
            <Grid container spacing={2} padding={1}>
              <Grid item xs={12}>
                <VTextField size='small' fullWidth name='mecanico' disabled={isLoading} label='Mecânico' placeholder='Nome de quem executou o serviço' />
              </Grid>
              <Grid item xs={12}>
                <VTextField size='small' fullWidth multiline rows={2} name='descricao' disabled={isLoading} label='Serviço Realizado' placeholder='Ex.: Troca de óleo e filtro' />
              </Grid>
              <Grid item xs={12} sm={6}>
                <VFieldMoneyNumber4dn
                  fullWidth
                  size='small'
                  name='valor'
                  label='Valor'
                  disabled={isLoading}
                  calcular={(novoValor) => setValor(Number(novoValor) || 0)}
                />
              </Grid>
              <Grid item xs={12} sm={6} display='flex' alignItems='center' justifyContent='flex-end'>
                <Typography variant='subtitle2'>
                  {valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </VForm>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Fechar</Button>
        <Button variant='contained' onClick={save} disabled={isLoading}>Adicionar</Button>
      </DialogActions>
    </Dialog>
  );
};
